import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../components/TopBar";
import { Modal, Field } from "./Fields";
import { api, fmtUSD, fmtNum } from "../lib/api";
import { useYear } from "../context/YearContext";
import AddableSelect from "../components/AddableSelect";
import { Plus, Trash2, Pencil, Tractor, Wheat, Printer, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const LOAN_TYPES = [
  { value: "operating_note", label: "Operating Note" },
  { value: "equipment_loan", label: "Equipment Loan" },
  { value: "mortgage", label: "Mortgage" },
  { value: "land_loan", label: "Land Loan" },
  { value: "other", label: "Other" },
];

const ASSET_TYPES = [
  { value: "land", label: "Land" },
  { value: "building", label: "Building" },
  { value: "improvement", label: "Improvement" },
  { value: "other", label: "Other" },
];

const blankLoan = { name: "", lender: "", loan_type: "operating_note", original_amount: "", current_balance: "", interest_rate: "", start_date: "", maturity_date: "", notes: "" };
const blankAsset = { name: "", asset_type: "land", value: "", value_as_of: "", acreage: "", location: "", purchase_date: "", notes: "" };

export default function BalanceSheet() {
  const { year } = useYear();
  const [data, setData] = useState(null);
  const [loans, setLoans] = useState([]);
  const [assets, setAssets] = useState([]);
  const [cropPrices, setCropPrices] = useState([]);

  const [loanOpen, setLoanOpen] = useState(false);
  const [loanForm, setLoanForm] = useState(blankLoan);
  const [editingLoan, setEditingLoan] = useState(null);

  const [assetOpen, setAssetOpen] = useState(false);
  const [assetForm, setAssetForm] = useState(blankAsset);
  const [editingAsset, setEditingAsset] = useState(null);

  const [priceOpen, setPriceOpen] = useState(false);
  const [priceForm, setPriceForm] = useState({ crop: "", market_price_per_unit: "", unit: "bushels", note: "" });

  const [cvOpen, setCvOpen] = useState(false);
  const [cvForm, setCvForm] = useState({ equipment_id: "", name: "", current_value: "", current_value_as_of: "" });

  const load = async () => {
    const [bs, l, a, cp] = await Promise.all([
      api.get(`/balance-sheet?year=${year}`),
      api.get(`/loans`),
      api.get(`/assets`),
      api.get(`/crop-prices`),
    ]);
    setData(bs.data);
    setLoans(l.data);
    setAssets(a.data);
    setCropPrices(cp.data);
  };

  useEffect(() => { load(); }, [year]);

  const cropsInBin = useMemo(() => {
    if (!data) return [];
    const set = new Set(data.assets.current.grain_inventory.by_crop.map((c) => c.crop));
    return Array.from(set);
  }, [data]);

  // ---------- Loan handlers ----------
  const submitLoan = async (e) => {
    e.preventDefault();
    const payload = {
      ...loanForm,
      original_amount: parseFloat(loanForm.original_amount) || 0,
      current_balance: parseFloat(loanForm.current_balance) || 0,
      interest_rate: parseFloat(loanForm.interest_rate) || 0,
    };
    try {
      if (editingLoan) await api.put(`/loans/${editingLoan.id}`, payload);
      else await api.post(`/loans`, payload);
      toast.success(editingLoan ? "Loan updated" : "Loan added");
      setLoanOpen(false); setEditingLoan(null); setLoanForm(blankLoan); load();
    } catch { toast.error("Could not save loan"); }
  };
  const editLoan = (l) => {
    setEditingLoan(l);
    setLoanForm({ ...blankLoan, ...l, original_amount: l.original_amount, current_balance: l.current_balance, interest_rate: l.interest_rate || "" });
    setLoanOpen(true);
  };
  const deleteLoan = async (id) => {
    if (!window.confirm("Delete this loan record?")) return;
    await api.delete(`/loans/${id}`); toast.success("Loan deleted"); load();
  };

  // ---------- Asset handlers ----------
  const submitAsset = async (e) => {
    e.preventDefault();
    const payload = {
      ...assetForm,
      value: parseFloat(assetForm.value) || 0,
      acreage: parseFloat(assetForm.acreage) || 0,
    };
    try {
      if (editingAsset) await api.put(`/assets/${editingAsset.id}`, payload);
      else await api.post(`/assets`, payload);
      toast.success(editingAsset ? "Asset updated" : "Asset added");
      setAssetOpen(false); setEditingAsset(null); setAssetForm(blankAsset); load();
    } catch { toast.error("Could not save asset"); }
  };
  const editAsset = (a) => {
    setEditingAsset(a);
    setAssetForm({ ...blankAsset, ...a, value: a.value, value_as_of: a.value_as_of || "", acreage: a.acreage || "" });
    setAssetOpen(true);
  };
  const deleteAsset = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    await api.delete(`/assets/${id}`); toast.success("Asset deleted"); load();
  };

  // ---------- Crop price handlers ----------
  const submitPrice = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/crop-prices`, {
        crop: priceForm.crop,
        market_price_per_unit: parseFloat(priceForm.market_price_per_unit) || 0,
        unit: priceForm.unit || "bushels",
        note: priceForm.note,
      });
      toast.success("Market price saved");
      setPriceOpen(false); setPriceForm({ crop: "", market_price_per_unit: "", unit: "bushels", note: "" }); load();
    } catch { toast.error("Could not save price"); }
  };
  const deletePrice = async (id) => {
    await api.delete(`/crop-prices/${id}`); load();
  };

  // ---------- Equipment current value handlers ----------
  const openCv = (row) => {
    setCvForm({
      equipment_id: row.id,
      name: row.name,
      current_value: row.current_value_override ? row.current_value : "",
      current_value_as_of: row.current_value_as_of || new Date().toISOString().slice(0, 10),
    });
    setCvOpen(true);
  };
  const submitCv = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/equipment/${cvForm.equipment_id}/current-value`, {
        current_value: parseFloat(cvForm.current_value) || 0,
        current_value_as_of: cvForm.current_value_as_of || "",
      });
      toast.success(parseFloat(cvForm.current_value) > 0 ? "Current value saved" : "Current value cleared — back to book value");
      setCvOpen(false); load();
    } catch { toast.error("Could not save current value"); }
  };
  const clearCv = async () => {
    try {
      await api.patch(`/equipment/${cvForm.equipment_id}/current-value`, { current_value: 0, current_value_as_of: "" });
      toast.success("Reverted to book value");
      setCvOpen(false); load();
    } catch { toast.error("Could not clear current value"); }
  };

  if (!data) {
    return <div><TopBar title="Balance Sheet" subtitle="Loading…" /></div>;
  }

  const { assets: A, liabilities: L, equity } = data;

  return (
    <div>
      <TopBar
        title="Balance Sheet"
        subtitle={`Snapshot of what you own vs. what you owe — as of ${data.as_of}.`}
        actions={
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <button data-testid="balance-sheet-print" onClick={() => window.print()} className="border border-[#1F1E1B] px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white transition-colors flex items-center gap-2">
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        }
      />

      {/* Top-line summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6 print-avoid" data-testid="balance-sheet-summary">
        <SummaryCard label="Total Assets" value={A.total} tone="positive" />
        <SummaryCard label="Total Liabilities" value={L.total} tone="negative" />
        <SummaryCard label="Owner Equity" value={equity} tone={equity >= 0 ? "highlight" : "negative"} />
      </div>

      {/* FMV staleness reminders */}
      {data.fmv_alerts && data.fmv_alerts.total_count > 0 && (
        <div
          className="border-2 border-[#C77E1A] bg-[#FFF6E5] p-4 mb-6 no-print"
          data-testid="fmv-alerts-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#C77E1A] mt-0.5 shrink-0" strokeWidth={2.2} />
            <div className="flex-1 min-w-0">
              <div className="font-display text-base font-black uppercase tracking-wider text-[#5C4A2B]">
                {data.fmv_alerts.total_count} fair-market value{data.fmv_alerts.total_count === 1 ? "" : "s"} need{data.fmv_alerts.total_count === 1 ? "s" : ""} refreshing
              </div>
              <div className="text-xs text-[#5C4A2B] mt-1">
                Values you set over a year ago (or land/buildings with no recorded date). Keeping these fresh keeps the balance sheet bank-ready.
              </div>
              <ul className="mt-2 space-y-1" data-testid="fmv-alerts-list">
                {data.fmv_alerts.items.slice(0, 8).map((it) => (
                  <li key={`${it.kind}-${it.id}`} className="flex items-baseline gap-2 text-xs">
                    <span className="bg-[#C77E1A] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 shrink-0">
                      {it.kind}
                    </span>
                    <span className="font-semibold truncate">{it.name}</span>
                    <span className="text-[#66645E] tabular shrink-0">{fmtUSD(it.current_value)}</span>
                    <span className="ml-auto text-[#66645E] shrink-0">
                      {it.as_of
                        ? `last set ${it.as_of}${it.age_days != null ? ` · ${it.age_days}d ago` : ""}`
                        : "no date on file"}
                    </span>
                    <button
                      type="button"
                      data-testid={`fmv-refresh-${it.kind}-${it.id}`}
                      onClick={() => {
                        if (it.kind === "equipment") {
                          // Find the equipment row in A.fixed.equipment.rows to reuse openCv
                          const row = A.fixed.equipment.rows.find((r) => r.id === it.id);
                          if (row) openCv(row);
                        } else {
                          const row = A.fixed.land_and_buildings.rows.find((r) => r.id === it.id);
                          if (row) editAsset(row);
                        }
                      }}
                      className="ml-2 border border-[#5C4A2B] text-[#5C4A2B] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider hover:bg-[#5C4A2B] hover:text-white whitespace-nowrap inline-flex items-center gap-1"
                    >
                      <RefreshCw className="w-2.5 h-2.5" /> Refresh
                    </button>
                  </li>
                ))}
                {data.fmv_alerts.items.length > 8 && (
                  <li className="text-[11px] text-[#66645E] italic">+ {data.fmv_alerts.items.length - 8} more in the tables below…</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="print-container bg-white border-2 border-[#1F1E1B] p-6 md:p-10" data-testid="balance-sheet-report">
        <div className="border-b-2 border-[#1F1E1B] pb-4 mb-6 print-avoid">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#66645E]">AcreLatch · Balance Sheet</div>
          <h2 className="font-display text-3xl font-black tracking-tight mt-1">As of {data.as_of}</h2>
          <div className="text-xs text-[#66645E] mt-1">Tax year {data.year} basis · Equipment book value uses {data.year} accumulated depreciation</div>
        </div>

        {/* ASSETS */}
        <div className="mb-8">
          <SectionHeader title="Assets" testid="bs-assets-header" />

          <SubSection title="Current Assets" icon={Wheat}>
            <Group title="Grain in storage" total={A.current.grain_inventory.total} testid="bs-grain">
              {A.current.grain_inventory.by_location.length === 0 ? (
                <EmptyRow>No grain on hand. Log a harvest with destination "Bin" to populate.</EmptyRow>
              ) : (
                <Table headers={["Bin", "Crop", "Quantity", "Price", "Source", "Value"]}>
                  {A.current.grain_inventory.by_location.map((r, i) => (
                    <tr key={i}>
                      <td>{r.location_name}</td>
                      <td>{r.crop}</td>
                      <td className="num">{fmtNum(r.bushels)} <span className="text-[10px] text-[#66645E]">{r.unit || "bushels"}</span></td>
                      <td className="num">{fmtUSD(r.price_per_unit)}</td>
                      <td className="text-xs uppercase text-[#66645E]">{(r.price_source || "").replace("_", " ")}</td>
                      <td className="num font-bold">{fmtUSD(r.value)}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </Group>

            <Group title="Pre-sold contracts (open balance)" total={A.current.contracts_outstanding.total} testid="bs-contracts">
              {A.current.contracts_outstanding.rows.length === 0 ? (
                <EmptyRow>No outstanding pre-sold contracts.</EmptyRow>
              ) : (
                <Table headers={["Buyer", "Crop", "Bushels Owed", "Price", "Value"]}>
                  {A.current.contracts_outstanding.rows.map((r) => (
                    <tr key={r.contract_id}>
                      <td>{r.buyer}</td>
                      <td>{r.crop}</td>
                      <td className="num">{fmtNum(r.outstanding_bushels)}</td>
                      <td className="num">{fmtUSD(r.price_per_unit)}</td>
                      <td className="num font-bold">{fmtUSD(r.value)}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </Group>

            <Group title="Livestock at current value" total={A.current.livestock.total} testid="bs-livestock">
              {A.current.livestock.by_species.length === 0 ? (
                <EmptyRow>No active livestock.</EmptyRow>
              ) : (
                <Table headers={["Species", "Head", "Purchase Cost", "Current Value"]}>
                  {A.current.livestock.by_species.map((r) => {
                    const cost = r.purchase_cost || 0;
                    const uplift = cost > 0 && r.value !== cost;
                    return (
                      <tr key={r.species}>
                        <td>{r.species}</td>
                        <td className="num">{r.head}</td>
                        <td className="num text-[#66645E]">{fmtUSD(cost)}</td>
                        <td className="num font-bold">
                          {fmtUSD(r.value)}
                          {uplift && (
                            <span className={`ml-1 text-[10px] ${r.value > cost ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
                              {r.value > cost ? "▲" : "▼"} {fmtUSD(Math.abs(r.value - cost))}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </Table>
              )}
            </Group>

            <SubTotal label="Total Current Assets" value={A.current.total} />
          </SubSection>

          <SubSection title="Fixed Assets" icon={Tractor}>
            <Group
              title="Equipment — current value"
              total={A.fixed.equipment.current_value_total}
              testid="bs-equipment"
              subtotalNote={A.fixed.equipment.current_value_total !== A.fixed.equipment.book_value_total
                ? `Book value would be ${fmtUSD(A.fixed.equipment.book_value_total)}`
                : "No fair-market overrides set — using book value"}
            >
              {A.fixed.equipment.rows.length === 0 ? (
                <EmptyRow>No equipment on file.</EmptyRow>
              ) : (
                <Table headers={["Equipment", "Yr", "Treatment", "Cost", "Acc. Depr", "Book Value", "Current Value", ""]}>
                  {A.fixed.equipment.rows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.name}</td>
                      <td className="num">{r.purchase_year}</td>
                      <td className="text-xs uppercase text-[#66645E]">{r.tax_treatment}</td>
                      <td className="num">{fmtUSD(r.cost)}</td>
                      <td className="num text-[#BC4727]">{fmtUSD(r.accumulated_depreciation)}</td>
                      <td className="num text-[#66645E]">{fmtUSD(r.book_value)}</td>
                      <td className="num font-bold">
                        <div className="inline-flex items-baseline gap-1.5 justify-end">
                          <span>{fmtUSD(r.current_value)}</span>
                          {r.current_value_override && (
                            <span
                              className="bg-[#1A4135] text-white text-[8px] font-bold uppercase tracking-wider px-1 py-0.5"
                              title={r.current_value_as_of ? `Fair-market value as of ${r.current_value_as_of}` : "Fair-market value override"}
                              data-testid={`equipment-fmv-badge-${r.id}`}
                            >
                              FMV
                            </span>
                          )}
                          {r.fmv_stale && (
                            <span
                              className="bg-[#C77E1A] text-white text-[8px] font-bold uppercase tracking-wider px-1 py-0.5"
                              title={r.current_value_as_of ? `Last refreshed ${r.current_value_as_of} — over a year ago` : "No refresh date on file"}
                              data-testid={`equipment-fmv-stale-${r.id}`}
                            >
                              Stale
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="no-print">
                        <button
                          data-testid={`set-equipment-fmv-${r.id}`}
                          onClick={() => openCv(r)}
                          className="border border-[#1F1E1B] px-2 py-1 text-[10px] font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white whitespace-nowrap"
                        >
                          {r.current_value_override ? "Edit FMV" : "Set FMV"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </Table>
              )}
            </Group>

            <Group
              title="Land & Buildings"
              total={A.fixed.land_and_buildings.total}
              testid="bs-land"
              action={
                <button data-testid="add-asset-btn" onClick={() => { setEditingAsset(null); setAssetForm(blankAsset); setAssetOpen(true); }} className="text-xs font-bold uppercase tracking-wider border border-[#1F1E1B] px-3 py-1 hover:bg-[#1F1E1B] hover:text-white flex items-center gap-1 no-print">
                  <Plus className="w-3 h-3" /> Add
                </button>
              }
            >
              {A.fixed.land_and_buildings.rows.length === 0 ? (
                <EmptyRow>No land or buildings entered yet. Click "Add" to enter a parcel or structure.</EmptyRow>
              ) : (
                <Table headers={["Name", "Type", "Acres", "Location", "Value", ""]}>
                  {A.fixed.land_and_buildings.rows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.name}</td>
                      <td className="text-xs uppercase text-[#66645E]">{r.asset_type}</td>
                      <td className="num">{r.acreage ? fmtNum(r.acreage, 2) : "—"}</td>
                      <td className="text-[#66645E]">{r.location || "—"}</td>
                      <td className="num font-bold">
                        <div className="inline-flex items-baseline gap-1.5 justify-end">
                          <span>{fmtUSD(r.value)}</span>
                          {r.fmv_stale && (
                            <span
                              className="bg-[#C77E1A] text-white text-[8px] font-bold uppercase tracking-wider px-1 py-0.5"
                              title={r.value_as_of ? `Last refreshed ${r.value_as_of} — over a year ago` : "No refresh date on file"}
                              data-testid={`land-fmv-stale-${r.id}`}
                            >
                              Stale
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="no-print">
                        <div className="flex gap-1.5 justify-end">
                          <button data-testid={`edit-asset-${r.id}`} onClick={() => editAsset(r)} className="p-1 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                          <button data-testid={`delete-asset-${r.id}`} onClick={() => deleteAsset(r.id)} className="p-1 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </Table>
              )}
            </Group>

            <SubTotal label="Total Fixed Assets" value={A.fixed.total} />
          </SubSection>

          <div className="border-t-2 border-[#1F1E1B] pt-3 mt-4 flex items-baseline justify-between print-avoid">
            <div className="font-display text-lg font-bold uppercase tracking-wider">Total Assets</div>
            <div className="font-display text-3xl font-black tabular text-[#1A4135]" data-testid="bs-assets-total">{fmtUSD(A.total)}</div>
          </div>
        </div>

        {/* LIABILITIES */}
        <div className="mb-8">
          <SectionHeader
            title="Liabilities"
            testid="bs-liabilities-header"
            action={
              <button data-testid="add-loan-btn" onClick={() => { setEditingLoan(null); setLoanForm(blankLoan); setLoanOpen(true); }} className="text-xs font-bold uppercase tracking-wider border border-[#1F1E1B] px-3 py-1 hover:bg-[#1F1E1B] hover:text-white flex items-center gap-1 no-print">
                <Plus className="w-3 h-3" /> Add Loan
              </button>
            }
          />
          {loans.length === 0 ? (
            <EmptyRow>Debt-free — nothing here. Add an operating note, equipment loan, or mortgage if any apply.</EmptyRow>
          ) : (
            <Table headers={["Name", "Lender", "Type", "Interest", "Original", "Current Balance", ""]}>
              {loans.map((r) => (
                <tr key={r.id}>
                  <td className="font-semibold">{r.name}</td>
                  <td>{r.lender || "—"}</td>
                  <td className="text-xs uppercase text-[#66645E]">{(r.loan_type || "").replace("_", " ")}</td>
                  <td className="num">{r.interest_rate ? `${r.interest_rate}%` : "—"}</td>
                  <td className="num">{fmtUSD(r.original_amount)}</td>
                  <td className="num font-bold text-[#BC4727]">{fmtUSD(r.current_balance)}</td>
                  <td className="no-print">
                    <div className="flex gap-1.5 justify-end">
                      <button data-testid={`edit-loan-${r.id}`} onClick={() => editLoan(r)} className="p-1 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                      <button data-testid={`delete-loan-${r.id}`} onClick={() => deleteLoan(r.id)} className="p-1 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
          <div className="border-t-2 border-[#1F1E1B] pt-3 mt-4 flex items-baseline justify-between print-avoid">
            <div className="font-display text-lg font-bold uppercase tracking-wider">Total Liabilities</div>
            <div className="font-display text-3xl font-black tabular text-[#BC4727]" data-testid="bs-liabilities-total">{fmtUSD(L.total)}</div>
          </div>
        </div>

        {/* EQUITY */}
        <div className="border-t-4 border-[#1F1E1B] pt-5 mt-6 print-avoid">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Owner Equity</div>
              <div className="text-sm text-[#66645E]">Total Assets minus Total Liabilities</div>
            </div>
            <div className={`font-display text-5xl font-black tabular ${equity >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`} data-testid="bs-equity-total">
              {fmtUSD(equity)}
            </div>
          </div>
        </div>
      </div>

      {/* Crop market prices side panel */}
      <div className="bg-white border border-[#1F1E1B] mt-6 p-5 no-print" data-testid="crop-prices-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#66645E]">Grain valuation</div>
            <div className="font-display text-lg font-bold">Market Price Overrides</div>
            <div className="text-xs text-[#66645E] mt-1">Set a current market price per crop. If none is set, the balance sheet uses your average sale price for the year.</div>
          </div>
          <button data-testid="add-crop-price-btn" onClick={() => { setPriceForm({ crop: cropsInBin[0] || "", market_price_per_unit: "", unit: "bushels", note: "" }); setPriceOpen(true); }} className="border border-[#1F1E1B] px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white flex items-center gap-1">
            <Plus className="w-3 h-3" /> Set Price
          </button>
        </div>
        {cropPrices.length === 0 ? (
          <div className="text-sm text-[#66645E] italic">No overrides set — using sale-price averages.</div>
        ) : (
          <Table headers={["Crop", "Price", "Unit", "Note", "Updated", ""]}>
            {cropPrices.map((p) => (
              <tr key={p.id}>
                <td className="font-semibold">{p.crop}</td>
                <td className="num">{fmtUSD(p.market_price_per_unit)}</td>
                <td>{p.unit}</td>
                <td className="text-[#66645E] text-xs">{p.note || "—"}</td>
                <td className="text-xs text-[#66645E]">{(p.updated_at || "").slice(0, 10)}</td>
                <td>
                  <button data-testid={`delete-crop-price-${p.id}`} onClick={() => deletePrice(p.id)} className="p-1 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </div>

      {/* Loan modal */}
      {loanOpen && (
        <Modal title={editingLoan ? "Edit Loan" : "Add Loan / Debt"} onClose={() => setLoanOpen(false)}>
          <form onSubmit={submitLoan} className="space-y-4" data-testid="loan-form">
            <Field label="Name" required><input data-testid="loan-name-input" required value={loanForm.name} onChange={(e) => setLoanForm({ ...loanForm, name: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Lender"><input data-testid="loan-lender-input" value={loanForm.lender} onChange={(e) => setLoanForm({ ...loanForm, lender: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <Field label="Type" required>
                <AddableSelect
                  domain="loan_type"
                  defaults={LOAN_TYPES}
                  value={loanForm.loan_type}
                  onChange={(v) => setLoanForm({ ...loanForm, loan_type: v })}
                  testid="loan-type-select"
                />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Original Amount" required><input data-testid="loan-original-input" type="number" step="0.01" required value={loanForm.original_amount} onChange={(e) => setLoanForm({ ...loanForm, original_amount: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" /></Field>
              <Field label="Current Balance" required><input data-testid="loan-balance-input" type="number" step="0.01" required value={loanForm.current_balance} onChange={(e) => setLoanForm({ ...loanForm, current_balance: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" /></Field>
              <Field label="Interest %"><input data-testid="loan-interest-input" type="number" step="0.01" value={loanForm.interest_rate} onChange={(e) => setLoanForm({ ...loanForm, interest_rate: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Start Date"><input data-testid="loan-start-input" type="date" value={loanForm.start_date} onChange={(e) => setLoanForm({ ...loanForm, start_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <Field label="Maturity Date"><input data-testid="loan-maturity-input" type="date" value={loanForm.maturity_date} onChange={(e) => setLoanForm({ ...loanForm, maturity_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            </div>
            <Field label="Notes"><textarea data-testid="loan-notes-input" rows={2} value={loanForm.notes} onChange={(e) => setLoanForm({ ...loanForm, notes: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="loan-cancel-btn" onClick={() => setLoanOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="loan-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Loan</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Asset modal */}
      {assetOpen && (
        <Modal title={editingAsset ? "Edit Land/Building" : "Add Land or Building"} onClose={() => setAssetOpen(false)}>
          <form onSubmit={submitAsset} className="space-y-4" data-testid="asset-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" required><input data-testid="asset-name-input" required value={assetForm.name} onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" placeholder="Home Quarter" /></Field>
              <Field label="Type" required>
                <AddableSelect
                  domain="asset_type"
                  defaults={ASSET_TYPES}
                  value={assetForm.asset_type}
                  onChange={(v) => setAssetForm({ ...assetForm, asset_type: v })}
                  testid="asset-type-select"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Value (USD)" required><input data-testid="asset-value-input" type="number" step="0.01" required value={assetForm.value} onChange={(e) => setAssetForm({ ...assetForm, value: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" /></Field>
              <Field label="Value as of">
                <input data-testid="asset-value-asof-input" type="date" value={assetForm.value_as_of} onChange={(e) => setAssetForm({ ...assetForm, value_as_of: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
                <div className="text-[10px] text-[#66645E] mt-1">Leave blank and today's date is used. Refreshing yearly avoids a "Stale" badge.</div>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Acreage (if land)"><input data-testid="asset-acreage-input" type="number" step="0.01" value={assetForm.acreage} onChange={(e) => setAssetForm({ ...assetForm, acreage: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" /></Field>
              <Field label="Location"><input data-testid="asset-location-input" value={assetForm.location} onChange={(e) => setAssetForm({ ...assetForm, location: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Purchase Date"><input data-testid="asset-date-input" type="date" value={assetForm.purchase_date} onChange={(e) => setAssetForm({ ...assetForm, purchase_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <div></div>
            </div>
            <Field label="Notes"><textarea data-testid="asset-notes-input" rows={2} value={assetForm.notes} onChange={(e) => setAssetForm({ ...assetForm, notes: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="asset-cancel-btn" onClick={() => setAssetOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="asset-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Asset</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Crop price modal */}
      {priceOpen && (
        <Modal title="Set Market Price" onClose={() => setPriceOpen(false)}>
          <form onSubmit={submitPrice} className="space-y-4" data-testid="crop-price-form">
            <Field label="Crop" required>
              <input data-testid="crop-price-crop-input" required value={priceForm.crop} onChange={(e) => setPriceForm({ ...priceForm, crop: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" placeholder="Corn" list="crop-suggestions" />
              <datalist id="crop-suggestions">
                {cropsInBin.map((c) => <option key={c} value={c} />)}
              </datalist>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Price / unit" required><input data-testid="crop-price-amount-input" type="number" step="0.0001" required value={priceForm.market_price_per_unit} onChange={(e) => setPriceForm({ ...priceForm, market_price_per_unit: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" /></Field>
              <Field label="Unit"><input data-testid="crop-price-unit-input" value={priceForm.unit} onChange={(e) => setPriceForm({ ...priceForm, unit: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            </div>
            <Field label="Note"><input data-testid="crop-price-note-input" value={priceForm.note} onChange={(e) => setPriceForm({ ...priceForm, note: e.target.value })} placeholder="Source / date checked" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="crop-price-cancel-btn" onClick={() => setPriceOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="crop-price-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Price</button>
            </div>
          </form>
        </Modal>
      )}
      {/* Equipment current-value (FMV) modal */}
      {cvOpen && (
        <Modal title={`Current Value — ${cvForm.name}`} onClose={() => setCvOpen(false)}>
          <form onSubmit={submitCv} className="space-y-4" data-testid="equipment-cv-form">
            <p className="text-xs text-[#66645E] -mt-1">
              Override the book value on the balance sheet with what this piece of equipment is actually worth today.
              Leave at zero (or clear it) to fall back to <em>cost − accumulated depreciation</em>.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Current Value (USD)" required>
                <input
                  data-testid="equipment-cv-value-input"
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={cvForm.current_value}
                  onChange={(e) => setCvForm({ ...cvForm, current_value: e.target.value })}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
                />
              </Field>
              <Field label="As Of">
                <input
                  data-testid="equipment-cv-asof-input"
                  type="date"
                  value={cvForm.current_value_as_of}
                  onChange={(e) => setCvForm({ ...cvForm, current_value_as_of: e.target.value })}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
                />
              </Field>
            </div>
            <div className="flex justify-between gap-2 pt-2">
              <button type="button" data-testid="equipment-cv-clear-btn" onClick={clearCv} className="border border-[#BC4727] text-[#BC4727] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#BC4727] hover:text-white">
                Use book value
              </button>
              <div className="flex gap-2">
                <button type="button" data-testid="equipment-cv-cancel-btn" onClick={() => setCvOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
                <button type="submit" data-testid="equipment-cv-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Value</button>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function SectionHeader({ title, testid, action }) {
  return (
    <div className="flex items-baseline justify-between border-b-2 border-[#1F1E1B] pb-2 mb-4 print-avoid" data-testid={testid}>
      <h3 className="font-display text-2xl font-black uppercase tracking-tight">{title}</h3>
      {action}
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  const color = tone === "positive" ? "text-[#1A4135]" : tone === "negative" ? "text-[#BC4727]" : "text-[#1F1E1B]";
  return (
    <div className="border border-[#1F1E1B] p-4 bg-white">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
      <div className={`font-display text-3xl font-black tabular mt-1 ${color}`}>{fmtUSD(value)}</div>
    </div>
  );
}

function SubSection({ title, icon: Icon, children }) {
  return (
    <div className="mb-6">
      <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#1F1E1B] mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-3 h-3" />} {title}
      </div>
      {children}
    </div>
  );
}

function Group({ title, total, children, testid, action, subtotalNote }) {
  return (
    <div className="mb-4 print-avoid" data-testid={testid}>
      <div className="flex items-baseline justify-between bg-[#F5F4F0] border border-[#D2CEC1] px-3 py-1.5">
        <span className="text-xs font-bold uppercase tracking-wider">{title}</span>
        <div className="flex items-center gap-3">
          {action}
          <span className="text-sm font-bold tabular">{fmtUSD(total)}</span>
        </div>
      </div>
      {subtotalNote && (
        <div className="text-[10px] text-[#66645E] italic px-3 py-1">{subtotalNote}</div>
      )}
      {children}
    </div>
  );
}

function SubTotal({ label, value }) {
  return (
    <div className="border-t border-[#1F1E1B] pt-2 mt-2 flex items-baseline justify-between">
      <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
      <span className="font-bold tabular">{fmtUSD(value)}</span>
    </div>
  );
}

function Table({ headers, children }) {
  return (
    <div className="border border-[#D2CEC1] overflow-x-auto">
      <table className="ledger-table">
        <thead>
          <tr>{headers.map((h) => <th key={h} className={h === "Bushels" || h === "Price" || h === "Value" || h === "Cost" || h === "Acc. Depr" || h === "Book Value" || h === "Current Value" || h === "Original" || h === "Current Balance" || h === "Acres" || h === "Head" || h === "Bushels Owed" || h === "Interest" ? "num" : ""}>{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function EmptyRow({ children }) {
  return (
    <div className="border border-dashed border-[#D2CEC1] p-4 text-center text-sm text-[#66645E] italic">{children}</div>
  );
}
